import { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  LockKeyhole, LogOut, RefreshCw, Loader2, GraduationCap, Mail, HandHeart,
  Handshake, MessageSquareHeart, Newspaper, Quote, Check, School,
} from "lucide-react";

const KEY_STORAGE = "lpf_admin_key";

const TABS = [
  { id: "enrollments", label: "Enrollments", icon: GraduationCap, endpoint: "/program-inquiries" },
  { id: "schools", label: "Afterschool", icon: School, endpoint: "/school-inquiries" },
  { id: "contacts", label: "Contacts", icon: Mail, endpoint: "/contact" },
  { id: "volunteers", label: "Volunteers", icon: HandHeart, endpoint: "/volunteers" },
  { id: "partners", label: "Partners", icon: Handshake, endpoint: "/partners" },
  { id: "surveys", label: "Surveys", icon: MessageSquareHeart, endpoint: "/surveys" },
  { id: "newsletter", label: "Newsletter", icon: Newspaper, endpoint: "/newsletter" },
  { id: "testimonials", label: "Testimonials", icon: Quote, endpoint: "/admin/testimonials" },
];

const COLUMNS = {
  enrollments: [["parent_name", "Parent"], ["email", "Email"], ["phone", "Phone"], ["child_age", "Child Age"], ["program", "Program"], ["message", "Message"], ["created_at", "Date"]],
  schools: [["school_name", "School"], ["contact_name", "Contact"], ["role", "Role"], ["email", "Email"], ["phone", "Phone"], ["num_students", "Students"], ["pathways", "Pathways"], ["message", "Message"], ["created_at", "Date"]],
  contacts: [["name", "Name"], ["email", "Email"], ["phone", "Phone"], ["organization", "Organization"], ["interest_area", "Interest"], ["message", "Message"], ["created_at", "Date"]],
  volunteers: [["name", "Name"], ["email", "Email"], ["phone", "Phone"], ["interests", "Interests"], ["availability", "Availability"], ["message", "Message"], ["created_at", "Date"]],
  partners: [["organization", "Organization"], ["contact_name", "Contact"], ["email", "Email"], ["phone", "Phone"], ["partner_type", "Type"], ["message", "Message"], ["created_at", "Date"]],
  surveys: [["name", "Name"], ["email", "Email"], ["program", "Program"], ["rating", "Rating"], ["recommend", "Recommend"], ["enjoyed", "Enjoyed"], ["improve", "Improve"], ["created_at", "Date"]],
  newsletter: [["email", "Email"], ["created_at", "Date"]],
  testimonials: [["name", "Name"], ["role", "Role"], ["quote", "Quote"], ["approved", "Approved"], ["created_at", "Date"]],
};

function fmt(key, val) {
  if (val === true) return "Yes";
  if (val === false) return "No";
  if (key === "created_at" && val) return new Date(val).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  if (key === "rating" && val) return `${val}/5`;
  return val || "—";
}

function Login({ onAuth }) {
  const [key, setKey] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.get("/program-inquiries", { headers: { "X-Admin-Key": key } });
      localStorage.setItem(KEY_STORAGE, key);
      onAuth(key);
    } catch {
      toast.error("Invalid admin key.");
    } finally {
      setLoading(false);
    }
  };
  return (
    <div className="min-h-[70vh] grid place-items-center px-5" data-testid="admin-login">
      <form onSubmit={submit} className="w-full max-w-sm rounded-3xl border border-slate-100 bg-white p-8 shadow-lg">
        <span className="grid h-14 w-14 place-items-center rounded-2xl bg-brandnavy text-white"><LockKeyhole className="h-7 w-7" /></span>
        <h1 className="mt-5 font-display text-2xl font-black text-brandnavy">Admin Dashboard</h1>
        <p className="mt-2 text-sm text-slate-500">Enter your admin key to view form submissions.</p>
        <input
          data-testid="admin-key-input"
          type="password"
          value={key}
          onChange={(e) => setKey(e.target.value)}
          placeholder="Admin key"
          className="mt-6 w-full rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-accent/40 focus:border-accent"
        />
        <button data-testid="admin-login-button" disabled={loading} className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-full bg-accent px-6 py-3.5 font-semibold text-white transition hover:bg-orange-400 disabled:opacity-60">
          {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <LockKeyhole className="h-5 w-5" />} Sign In
        </button>
      </form>
    </div>
  );
}

export default function AdminDashboard() {
  const [authKey, setAuthKey] = useState(() => localStorage.getItem(KEY_STORAGE) || "");
  const [active, setActive] = useState("enrollments");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [counts, setCounts] = useState({});

  const tab = TABS.find((t) => t.id === active);

  const load = useCallback(async (tabId, key) => {
    const t = TABS.find((x) => x.id === tabId);
    setLoading(true);
    try {
      const { data } = await api.get(t.endpoint, { headers: { "X-Admin-Key": key } });
      setRows(Array.isArray(data) ? data : []);
      setCounts((c) => ({ ...c, [tabId]: Array.isArray(data) ? data.length : 0 }));
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error("Session expired. Please sign in again.");
        localStorage.removeItem(KEY_STORAGE);
        setAuthKey("");
      } else {
        toast.error("Failed to load data.");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (authKey) load(active, authKey);
  }, [authKey, active, load]);

  const approve = async (id) => {
    try {
      await api.patch(`/testimonials/${id}/approve`, {}, { headers: { "X-Admin-Key": authKey } });
      toast.success("Testimonial approved and published.");
      load("testimonials", authKey);
    } catch {
      toast.error("Could not approve.");
    }
  };

  const logout = () => {
    localStorage.removeItem(KEY_STORAGE);
    setAuthKey("");
  };

  if (!authKey) return <Login onAuth={setAuthKey} />;

  const cols = COLUMNS[active];

  return (
    <div className="min-h-[80vh] bg-slate-50" data-testid="admin-dashboard">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 py-10">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="font-display text-3xl font-black text-brandnavy">Submissions Dashboard</h1>
            <p className="mt-1 text-sm text-slate-500">All form activity from your website in one place.</p>
          </div>
          <div className="flex gap-2">
            <button data-testid="admin-refresh" onClick={() => load(active, authKey)} className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-100">
              <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Refresh
            </button>
            <button data-testid="admin-logout" onClick={logout} className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-100">
              <LogOut className="h-4 w-4" /> Sign Out
            </button>
          </div>
        </div>

        <div className="mt-8 flex flex-wrap gap-2">
          {TABS.map((t) => (
            <button
              key={t.id}
              data-testid={`admin-tab-${t.id}`}
              onClick={() => setActive(t.id)}
              className={`inline-flex items-center gap-2 rounded-full px-4 py-2.5 text-sm font-semibold transition ${
                active === t.id ? "bg-brandnavy text-white" : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-100"
              }`}
            >
              <t.icon className="h-4 w-4" /> {t.label}
              {counts[t.id] != null && (
                <span className={`ml-1 rounded-full px-2 py-0.5 text-xs ${active === t.id ? "bg-white/20" : "bg-slate-100"}`}>{counts[t.id]}</span>
              )}
            </button>
          ))}
        </div>

        <div className="mt-6 overflow-hidden rounded-3xl border border-slate-100 bg-white shadow-sm">
          {loading ? (
            <div className="grid place-items-center py-24 text-slate-400"><Loader2 className="h-8 w-8 animate-spin" /></div>
          ) : rows.length === 0 ? (
            <div className="grid place-items-center py-24 text-center text-slate-400">
              <tab.icon className="h-10 w-10" />
              <p className="mt-3 font-semibold">No {tab.label.toLowerCase()} yet.</p>
              <p className="text-sm">New submissions will show up here automatically.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm" data-testid="admin-table">
                <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    {cols.map(([k, label]) => <th key={k} className="whitespace-nowrap px-4 py-3 font-semibold">{label}</th>)}
                    {active === "testimonials" && <th className="px-4 py-3 font-semibold">Action</th>}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {rows.map((r) => (
                    <tr key={r.id} className="align-top hover:bg-slate-50/60">
                      {cols.map(([k]) => (
                        <td key={k} className="max-w-[240px] px-4 py-3 text-slate-700">{fmt(k, r[k])}</td>
                      ))}
                      {active === "testimonials" && (
                        <td className="px-4 py-3">
                          {r.approved ? (
                            <span className="inline-flex items-center gap-1 text-xs font-semibold text-green-600"><Check className="h-3.5 w-3.5" /> Live</span>
                          ) : (
                            <button data-testid={`approve-${r.id}`} onClick={() => approve(r.id)} className="rounded-full bg-accent px-3 py-1.5 text-xs font-semibold text-white hover:bg-orange-400">Approve</button>
                          )}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
